/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, ViewContainerRef } from '@angular/core';
import { ControlOn, AppFormControl } from '@src/app/shared/form/app-form-control';
import { ButtonField } from '@src/app/shared/field/ButtonField';
import { MyInjector } from '@src/app/app.module';
import { RouterLink } from '@angular/router';
import { AbstractObject } from '@src/app/model/abstract-object';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgTemplateOutlet } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldType } from '@src/app/shared/field/Field';

export class ButtonControl extends AppFormControl {
    field: ButtonField;
}

@Component({
    selector: 'app-form-button',
    templateUrl: './form-button.component.html',
    styleUrls: ['./form-button.component.scss'],
    imports: [NgTemplateOutlet, MatFormFieldModule, MatButtonModule, MatTooltipModule, RouterLink,
        MatInputModule, FormsModule, ReactiveFormsModule, MatIconModule]
})
export class FormButtonComponent {

    @Input() control: ButtonControl;
    @Input() on: ControlOn = 'form';

    public static makePageButton(label: string, method: () => void, icon: string = null): AppFormControl {
        const fld = FormButtonComponent.make(label, 'none', { type: 'icon' });
        fld.btnOpts.clickMethod = method;

        const ctl = fld.makeControl() as AppFormControl;
        ctl.setValue(icon);
        return ctl;
    }

    public static makeTextButton(label: string, value: string, method: (cell: AppFormControl) => void, options: Partial<ButtonField> = {}): ButtonField {
        const field = new ButtonField({ value, label, sendServer: false, type: 'link' }, options);
        field.btnOpts.clickMethod = method;
        field.formControlFactory = FormButtonComponent.createComponent;
        return field;
    }

    public static makeIconButton(name: string, icon: string | ((o: AbstractObject) => string), toolTip: string, method: (cell: AppFormControl) => void, label = ''): ButtonField {
        // name becomes a data-field-name attribute and a starlight dictionary key - strip anything
        // that isn't safe as a token (spaces, punctuation) regardless of what's passed in. label is
        // just on-screen text and is used exactly as given.
        const safeName = name.replace(/[^A-Za-z0-9_]/g, '');
        const btn = FormButtonComponent.make('', '', {
            name: safeName, label, type: 'icon', sendServer: false,
            cellOpts: {},
            calculateValue: () => 'block',
            toolTip,
            btnOpts: { clickMethod: method }
        });
        if (typeof icon === 'function') {
            btn.calculateValue = icon;
        } else {
            btn.calculateValue = () => icon;
        }
        return btn;
    }

    public static makeTableButton(label: string, value: string, method: (item: AbstractObject) => void, options: Partial<ButtonField> = {}, type: FieldType = 'link'): ButtonField {
        const field = new ButtonField({ value, label, sendServer: false, type }, options);
        field.btnOpts.tableClickMethod = method;
        field.formControlFactory = FormButtonComponent.createComponent;
        return field;
    }

    public static makeDialogOpenButton(label: string, value: string, dataValue: string,
    comp: ComponentType<AbstractPageComponent>,
    options: Partial<FormButtonComponent> = {}, hideTabs = true) {
        const defaultData = (i) => ({id: i[dataValue], forceTeamId: i.teamId, hideTabs});

        const field = new ButtonField({ value, label, sendServer: false, type: 'link', ...options });
        field.btnOpts.tableClickMethod = (i: AbstractObject) => {
            MyInjector.instance.get(MatDialog).open(comp,
                {
                    data: defaultData(i)
                }
            )
        }
        return field;
    }
    
    public outModed() {
        console.warn('Using outmoded navigation button', this.control?.field);
        return '';
    }

    public static make(heading: string, value: string, options: Partial<ButtonField> = {}): ButtonField {
        if (options.btnOpts?.navigates) {
            options.type = 'link';
            console.warn('Use makeLink for this...', { heading, value, options });
        }
        if (options.type !== 'link' && options.type !== 'icon') {
            console.error('This component does not know how to deal with field data supplied', options);
        }

        const field = new ButtonField({ value, cellOpts: { heading }, sendServer: false }, options);
        field.formControlFactory = FormButtonComponent.createComponent;
        return field;
    }

    public static createComponent(vcr: ViewContainerRef, ctl: ButtonControl, on: ControlOn) {
        const componentRef = vcr.createComponent(FormButtonComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }

    onClick($event) {
        // Disabled buttons do not show tooltip, we need tooltip for budget icons... but do not allow click readonly button
        if (this.control.field.btnOpts.clickMethod && !this.control.field.disable) {
            this.control.field.btnOpts.clickMethod(this.control, $event);
        }
    }

    image() {
        let theReturn: string = null;
        if (this.control?.value) {
            const val: string = this.control.value;
            if (val.startsWith('image:')) {
                theReturn = val.substring(6);
            }
        }
        return theReturn;
    }
}
